import React from 'react';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

import { isLoggedInUserEngineer } from '@paragon/login-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { useAppMonitor } from '../../../../monitor';
import {
  getLockOptionsFromComponentDesign,
  LockOptions,
  setLockOptionForComponentDesign,
} from '../../../../store/actions/lockOptionsActions';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { SceneMenuItem } from '../common/SceneMenuItem';

type Scope =
  | { context: 'container'; type: 'hanger' | 'platePair' | 'loadCase' | 'member' | 'brace' }
  | { context: 'single'; type: 'analogModel' };

function getLockIdentifier(scope: Scope): keyof LockOptions | null {
  switch (scope.type) {
    case 'analogModel':
      return 'analogLocked';

    case 'hanger':
      return 'hangersLocked';

    case 'platePair':
      return 'platingLocked';

    case 'loadCase':
      return 'loadingLocked';

    case 'member':
    case 'brace':
      return 'membersLocked';

    default:
      assertExhaustiveSwitch(scope);
  }
}

function toUserVisibleString(scope: Scope) {
  switch (scope.context) {
    case 'container': {
      switch (scope.type) {
        case 'hanger':
          return 'Hangers';

        case 'platePair':
          return 'Plate Pairs';

        case 'loadCase':
          return 'Load Cases';

        case 'member':
          return 'Members';

        case 'brace':
          return 'Braces';

        default:
          assertExhaustiveSwitch(scope);
      }
    }

    case 'single': {
      switch (scope.type) {
        case 'analogModel':
          return 'Analog Model';

        default:
          assertExhaustiveSwitch(scope);
      }
    }

    default:
      assertExhaustiveSwitch(scope);
  }
}

export function ToggleLockingMenuItem(props: Scope) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const userIsEngineer = useAppSelector(isLoggedInUserEngineer);
  const componentDesign = useAppSelector(getActiveComponentDesign)!;

  const lockOptions = getLockOptionsFromComponentDesign(componentDesign);

  const identifier = getLockIdentifier(props);
  if (identifier == null) return null;

  const disabledBecauseUserIsNotAnEngineer =
    (identifier === 'analogLocked' || identifier === 'loadingLocked') && !userIsEngineer;
  const disabledBecauseLoadingIsLocked = identifier === 'analogLocked' && lockOptions.loadingLocked;
  const disabled = disabledBecauseUserIsNotAnEngineer || disabledBecauseLoadingIsLocked;

  const locked = lockOptions[identifier];

  return (
    <SceneMenuItem
      icon={locked ? <LockIcon /> : <LockOpenIcon />}
      text={`${locked ? 'Unlock' : 'Lock'} ${props.context === 'container' ? 'All ' : ''}${toUserVisibleString(props)}`}
      disabled={disabled}
      onClick={() => {
        dispatch(setLockOptionForComponentDesign(componentDesign.guid, identifier, !locked));
        monitor.recordInteraction(`${toUserVisibleString(props)} locking toggled via menu item`);
      }}
    />
  );
}
